import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader } from "@/components/common";
import { useApi, errMsg } from "@/lib/api";
import { SPECS } from "@/lib/labels";

const EMPTY = { title: "", specialization: "elektryk", location: "", country: "", site_type: "onshore", rotation: "", rate: "", contract: "", start_date: "", description: "", requirements: [], active: true };

const Field = ({ label, children, className = "" }) => <div className={`space-y-1.5 ${className}`}><Label>{label}</Label>{children}</div>;

export default function JobForm() {
  const { id } = useParams();
  const api = useApi();
  const nav = useNavigate();
  const [f, setF] = useState(id ? null : EMPTY);
  const [reqText, setReqText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (id) api("get", `/jobs/${id}`).then((j) => { setF({ ...EMPTY, ...j }); setReqText((j.requirements || []).join("\n")); });
  }, [id, api]);
  if (!f) return <Loader />;
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const { title, specialization, location, country, site_type, rotation, rate, contract, start_date, description, active } = f;
    const body = { title, specialization, location, country, site_type, rotation, rate, contract, start_date, description, active, requirements: reqText.split("\n").map((s) => s.trim()).filter(Boolean) };
    try {
      const j = await api(id ? "put" : "post", id ? `/jobs/${id}` : "/jobs", body);
      toast.success(id ? "Job updated" : "Job published");
      nav(`/jobs/${j.id}`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="max-w-3xl" data-testid="job-form">
      <p className="eyebrow">{id ? "Editing" : "New job"}</p>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight">{id ? "Edit job" : "Post a job"}</h1>
      <div className="mt-8 panel p-6 grid sm:grid-cols-2 gap-5">
        <Field label="Position *" className="sm:col-span-2"><Input data-testid="job-form-title" value={f.title} onChange={set("title")} required minLength={3} placeholder="e.g. Wind turbine electrician" /></Field>
        <Field label="Specialization">
          <Select value={f.specialization} onValueChange={set("specialization")}>
            <SelectTrigger data-testid="job-form-specialization"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(SPECS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field label="Site type">
          <Select value={f.site_type} onValueChange={set("site_type")}>
            <SelectTrigger data-testid="job-form-site-type"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="onshore">Onshore</SelectItem><SelectItem value="offshore">Offshore</SelectItem></SelectContent>
          </Select>
        </Field>
        <Field label="Location *"><Input data-testid="job-form-location" value={f.location} onChange={set("location")} required minLength={2} /></Field>
        <Field label="Country"><Input data-testid="job-form-country" value={f.country} onChange={set("country")} /></Field>
        <Field label="Rotation"><Input data-testid="job-form-rotation" value={f.rotation} onChange={set("rotation")} placeholder="e.g. 2/2, 3/1" /></Field>
        <Field label="Rate"><Input data-testid="job-form-rate" value={f.rate} onChange={set("rate")} placeholder="e.g. 40 €/h" /></Field>
        <Field label="Contract type"><Input data-testid="job-form-contract" value={f.contract} onChange={set("contract")} /></Field>
        <Field label="Start"><Input data-testid="job-form-start" value={f.start_date} onChange={set("start_date")} placeholder="e.g. ASAP" /></Field>
        <Field label="Description" className="sm:col-span-2"><Textarea data-testid="job-form-description" rows={5} value={f.description} onChange={set("description")} /></Field>
        <Field label="Requirements (one per line)" className="sm:col-span-2"><Textarea data-testid="job-form-requirements" rows={4} value={reqText} onChange={(e) => setReqText(e.target.value)} placeholder={"GWO BST\nIRATA L2"} /></Field>
        <label className="flex items-center gap-3 sm:col-span-2">
          <Switch data-testid="job-form-active" checked={f.active} onCheckedChange={set("active")} />
          <span className="text-sm">Job active (visible to technicians)</span>
        </label>
      </div>
      <div className="mt-6 flex gap-3">
        <Button type="submit" disabled={busy} data-testid="job-form-submit" className="h-11 px-6">{busy ? "Saving..." : id ? "Save changes" : "Publish job"}</Button>
        <Button type="button" variant="ghost" onClick={() => nav(-1)} data-testid="job-form-cancel">Cancel</Button>
      </div>
    </form>
  );
}
